import type { ComponentProps } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'
import type TextButton from 'LuxKit/Button/TextButton'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addToUserCart } from 'actions/UserCartActions'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import OfferCheckoutButton from './OfferCheckoutButton'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { addToCartWithContext, hotelItemContextByPackageView } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { isNonNullable } from 'lib/array/arrayUtils'

interface Props extends Omit<ComponentProps<typeof TextButton>, 'kind' | 'children'> {
  packageView: App.OfferPackageOptionView
  overrideRooms?: Array<App.RoomOccupants | App.Occupants>
  onSubmit?: () => void
}

function OfferAddToCartButton(props: Props) {
  const {
    packageView,
    onSubmit,
    overrideRooms,
    ...rest
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()

  const onProceedToBooking = (items: Array<App.Checkout.AnyItem>) => {
    const nonCharteredFlights = items.some(item => isFlightItem(item) && !item.charterBundleRoute)
    const charteredFlights = items.some(item => isFlightItem(item) && item.charterBundleRoute)

    if (nonCharteredFlights) {
      // Pick regular flow and final decision to pay or only add to cart to be made at end of flight selection
      dispatch(initialiseCheckout(items, { mode: 'vertical-select' }))
      dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    } else if (charteredFlights) {
      const itemsReadyToAdd = items.filter(item => (item.itemType === 'flight' && item.flights.length > 0) || (item.itemType !== 'flight'))

      dispatch(addToUserCart(itemsReadyToAdd))

      const contextItems = items
        .map(item => hotelItemContextByPackageView(item, packageView, true))
        .filter(isNonNullable)

      Analytics.trackEvent(addToCartWithContext(contextItems))
    } else {
      const contextItems = items
        .map(item => hotelItemContextByPackageView(item, packageView, true))
        .filter(isNonNullable)
      Analytics.trackEvent(addToCartWithContext(contextItems))

      dispatch(addToUserCart(items))
    }
  }

  return (
    <OfferCheckoutButton
      {...rest}
      kind="secondary"
      startIcon={<LineShoppingCartIcon />}
      onSubmit={onSubmit}
      overrideRooms={overrideRooms}
      packageView={packageView}
      onProceedToBooking={onProceedToBooking}
      upgradeEnabled={false}
      disablePrefetchExperiences
    >
      {intl.formatMessage({
        id: 'irUvnz+zXN',
        defaultMessage: 'Add to cart',
        description: 'Button text to add offer to shopping cart in the cart view',
      })}
    </OfferCheckoutButton>
  )
}

export default OfferAddToCartButton
